import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setShowBanner(false);
    
    // Enable analytics and other services
    enableServices(consentData);
  };

  const handleRejectAll = () => {
    const consentData = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setShowBanner(false);
    
    // Only enable essential services
    enableServices(consentData);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Enable services based on preferences
    enableServices(consentData);
  };

  const enableServices = (consent: any) => {
    // Enable Google Analytics if analytics consent is given
    if (consent.analytics && typeof window !== 'undefined') {
      // Initialize Google Analytics
      (window as any).gtag?.('consent', 'update', {
        analytics_storage: 'granted',
      });
    }

    // Enable other services based on consent
    if (consent.functional) {
      // Enable functional cookies
    }

    if (consent.marketing) {
      // Enable marketing cookies
    }
  };

  const handlePreferenceChange = (type: keyof typeof preferences) => {
    if (type === 'essential') return; // Cannot disable essential cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                🍪 We use cookies to enhance your experience
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                We use cookies and similar technologies to provide essential functionality, 
                analyze usage, and improve our service. By continuing to use our site, 
                you consent to our use of cookies.{' '}
                <Link 
                  to="/cookie-policy" 
                  className="text-primary-400 hover:text-primary-300 underline"
                >
                  Learn more
                </Link>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 min-w-fit">
              <button
                onClick={handleCustomize}
                className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-600 rounded-macos hover:bg-gray-800 hover:text-white transition-colors"
              >
                Customize
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-600 rounded-macos hover:bg-gray-800 hover:text-white transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos hover:from-primary-600 hover:to-orange-600 transition-all shadow-sm"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity"
              onClick={() => setShowPreferences(false)}
            />

            {/* Modal */}
            <div className="inline-block align-bottom bg-gray-800 rounded-macos-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700">
              <div className="bg-gray-800 px-6 pt-6 pb-4">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Cookie Preferences
                </h3>
                <p className="text-gray-300 text-sm mb-6">
                  Choose which cookies you'd like to allow. Essential cookies are required for the site to function.
                </p>

                <div className="space-y-4">
                  {/* Essential Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-900/50 rounded-macos border border-gray-600">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">Essential Cookies</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Required for basic site functionality, security, and user authentication.
                      </p>
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-6 bg-primary-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Always On</p>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-900/50 rounded-macos border border-gray-600">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">Analytics Cookies</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Help us understand how you use our site to improve performance.
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handlePreferenceChange('analytics')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.analytics 
                            ? 'bg-primary-500 justify-end' 
                            : 'bg-gray-600 justify-start'
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-900/50 rounded-macos border border-gray-600">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">Functional Cookies</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Remember your preferences and provide enhanced features.
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handlePreferenceChange('functional')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.functional 
                            ? 'bg-primary-500 justify-end' 
                            : 'bg-gray-600 justify-start'
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-start justify-between p-4 bg-gray-900/50 rounded-macos border border-gray-600">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">Marketing Cookies</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Show you relevant ads and measure campaign effectiveness.
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handlePreferenceChange('marketing')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.marketing 
                            ? 'bg-primary-500 justify-end' 
                            : 'bg-gray-600 justify-start'
                        } px-1`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end border-t border-gray-700">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-600 rounded-macos hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos hover:from-primary-600 hover:to-orange-600 transition-all shadow-sm"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;